import React, { useEffect, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import API_BASE_URL from '../config/apiConfig';
import ProfileAccountSection from './ProfileAccountSection';
import AddressBookSection from './AddressBookSection';
import { openEcOrderPdf } from '../utils/openOrderPdf';

function MyAccount() {
    const navigate = useNavigate();
    const location = useLocation();
    const { user, token, logout } = useAuth();
    const [orders, setOrders] = useState([]);
    const [isOrdersLoading, setIsOrdersLoading] = useState(false);
    const [ordersError, setOrdersError] = useState('');
    const [pdfOrderId, setPdfOrderId] = useState(null);

    useEffect(() => {
        const fetchOrders = async () => {
            if (!user?.UserId || !token) {
                setOrders([]);
                return;
            }

            setIsOrdersLoading(true);
            setOrdersError('');

            try {
                const response = await fetch(`${API_BASE_URL}/ec/order/getByUser/${user.UserId}`, {
                    headers: {
                        Authorization: `Bearer ${token}`,
                    },
                });

                const result = await response.json();

                if (!response.ok || !result?.success) {
                    setOrdersError(result?.message || 'Unable to fetch orders.');
                    setOrders([]);
                    return;
                }

                setOrders(Array.isArray(result.data) ? result.data : []);
            } catch (_) {
                setOrdersError('Unable to reach server. Please try again.');
                setOrders([]);
            } finally {
                setIsOrdersLoading(false);
            }
        };

        fetchOrders();
    }, [user?.UserId, token]);

    const formatOrderDate = (value) => {
        if (!value) {
            return '-';
        }

        const parsedDate = new Date(value);
        if (Number.isNaN(parsedDate.getTime())) {
            return value;
        }

        return parsedDate.toLocaleDateString('en-IN', {
            day: '2-digit',
            month: 'short',
            year: 'numeric',
        });
    };

    const formatCurrency = (value) => {
        const numericValue = Number(value || 0);
        return numericValue.toLocaleString('en-IN', {
            minimumFractionDigits: 2,
            maximumFractionDigits: 2,
        });
    };

    const handleLogout = () => {
        logout();
        navigate('/login', { replace: true });
    };

    const handleViewOrder = async (order) => {
        if (!order?.OrderId || !token || pdfOrderId) {
            return;
        }

        setPdfOrderId(order.OrderId);
        setOrdersError('');

        try {
            await openEcOrderPdf(order.OrderId, token);
        } catch (error) {
            setOrdersError(error.message || 'Unable to open the order invoice.');
        } finally {
            setPdfOrderId(null);
        }
    };

  return (
    <div className="page-content pt-20 pb-20">
            <div className="container">
                <div className="row">
                    <div className="col-lg-10 m-auto">
                        <div className="row">
                            <div className="col-md-3">
                                <div className="dashboard-menu">
                                    <ul className="nav flex-column" role="tablist">
                                        <li className="nav-item">
                                            <a className="nav-link active" id="dashboard-tab" data-bs-toggle="tab"
                                                href="#dashboard" role="tab" aria-controls="dashboard"
                                                aria-selected="false"><i
                                                    className="fi-rs-settings-sliders mr-10"></i>Dashboard</a>
                                        </li>
                                        <li className="nav-item">
                                            <a className="nav-link" id="orders-tab" data-bs-toggle="tab" href="#orders"
                                                role="tab" aria-controls="orders" aria-selected="false"><i
                                                    className="fi-rs-shopping-bag mr-10"></i>Orders</a>
                                        </li>
                                        <li className="nav-item">
                                            <a className="nav-link" id="track-orders-tab" data-bs-toggle="tab"
                                                href="#track-orders" role="tab" aria-controls="track-orders"
                                                aria-selected="false"><i
                                                    className="fi-rs-shopping-cart-check mr-10"></i>Track Your Order</a>
                                        </li>
                                        <li className="nav-item">
                                            <a className="nav-link" id="address-tab" data-bs-toggle="tab" href="#address"
                                                role="tab" aria-controls="address" aria-selected="true"><i
                                                    className="fi-rs-marker mr-10"></i>My Address</a>
                                        </li>
                                        <li className="nav-item">
                                            <a className="nav-link" id="account-detail-tab" data-bs-toggle="tab"
                                                href="#account-detail" role="tab" aria-controls="account-detail"
                                                aria-selected="true"><i className="fi-rs-user mr-10"></i>Account details</a>
                                        </li>
                                        <li className="nav-item">
                                            <button type="button" className="nav-link border-0 bg-transparent text-start w-100" onClick={handleLogout}><i
                                                    className="fi-rs-sign-out mr-10"></i>Logout</button>
                                        </li>
                                    </ul>
                                </div>
                            </div>
                            <div className="col-md-9">
                                {location.state?.completeProfile || location.state?.message ? (
                                    <div className="alert alert-info mb-20">
                                        {location.state?.message || 'Please complete your profile: verify email/mobile, add address, and set a password if needed.'}
                                    </div>
                                ) : null}
                                <div className="tab-content account dashboard-content pl-50">
                                    <div className="tab-pane fade active show" id="dashboard" role="tabpanel"
                                        aria-labelledby="dashboard-tab">
                                        <div className="card">
                                            <div className="card-header">
                                                <h3 className="mb-0">Hello {user?.UserName || 'User'}!</h3>
                                            </div>
                                            <div className="card-body">
                                                <p>
                                                    From your account dashboard, you can check your profile details and account preferences.<br/>
                                                    Registered email: <strong>{user?.Email || '-'}</strong>
                                                </p>
                                            </div>
                                        </div>
                                    </div>
                                    <div className="tab-pane fade" id="orders" role="tabpanel" aria-labelledby="orders-tab">
                                        <div className="card">
                                            <div className="card-header">
                                                <h3 className="mb-0">Your Orders</h3>
                                            </div>
                                            <div className="card-body">
                                                {ordersError ? <p className="text-danger mb-20">{ordersError}</p> : null}
                                                <div className="table-responsive">
                                                    <table className="table">
                                                        <thead>
                                                            <tr>
                                                                <th>Order</th>
                                                                <th>Date</th>
                                                                <th>Status</th>
                                                                <th>Total</th>
                                                                <th>Invoice</th>
                                                            </tr>
                                                        </thead>
                                                        <tbody>
                                                            {isOrdersLoading ? (
                                                                <tr>
                                                                    <td colSpan="5" className="text-center">Loading your orders...</td>
                                                                </tr>
                                                            ) : null}
                                                            {!isOrdersLoading && !orders.length ? (
                                                                <tr>
                                                                    <td colSpan="5" className="text-center">No orders found for your account.</td>
                                                                </tr>
                                                            ) : null}
                                                            {!isOrdersLoading && orders.map((order) => (
                                                                <tr key={order.OrderId}>
                                                                    <td>{order.OrderNumber || `#${order.OrderId}`}</td>
                                                                    <td>{formatOrderDate(order.OrderDate)}</td>
                                                                    <td>{order.OrderStatus || '-'}</td>
                                                                    <td><i className="fa fa-inr" aria-hidden="true"></i>{formatCurrency(order.TotalAmount)} for {order.ItemCount || 0} item(s)</td>
                                                                    <td>
                                                                        <button
                                                                            type="button"
                                                                            className="btn-small d-block border-0"
                                                                            disabled={pdfOrderId === order.OrderId}
                                                                            onClick={() => handleViewOrder(order)}
                                                                        >
                                                                            {pdfOrderId === order.OrderId ? 'Opening...' : 'View'}
                                                                        </button>
                                                                    </td>
                                                                </tr>
                                                            ))}
                                                        </tbody>
                                                    </table>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                    <div className="tab-pane fade" id="track-orders" role="tabpanel"
                                        aria-labelledby="track-orders-tab">
                                        <div className="card">
                                            <div className="card-header">
                                                <h3 className="mb-0">Orders tracking</h3>
                                            </div>
                                            <div className="card-body contact-from-area">
                                                <p>To track your order please enter your OrderID in the box below and
                                                    press "Track" button. This was given to you on your receipt and in
                                                    the confirmation email you should have received.</p>
                                                <div className="row">
                                                    <div className="col-lg-8">
                                                        <form className="contact-form-style mt-30 mb-50" action="#"
                                                            method="post">
                                                            <div className="input-style mb-20">
                                                                <label>Order ID</label>
                                                                <input name="order-id"
                                                                    placeholder="Found in your order confirmation email"
                                                                    type="text"/>
                                                            </div>
                                                            <div className="input-style mb-20">
                                                                <label>Billing email</label>
                                                                <input name="billing-email"
                                                                    placeholder="Email you used during checkout"
                                                                    type="email"/>
                                                            </div>
                                                            <button className="submit submit-auto-width"
                                                                type="submit">Track</button>
                                                        </form>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                    <div className="tab-pane fade" id="address" role="tabpanel"
                                        aria-labelledby="address-tab">
                                        <AddressBookSection user={user} token={token} />
                                    </div>
                                    <div className="tab-pane fade" id="account-detail" role="tabpanel"
                                        aria-labelledby="account-detail-tab">
                                        <ProfileAccountSection />
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
  )
}

export default MyAccount
